import React from 'react';
import { motion } from 'framer-motion';
import { Coins, Shield, TrendingUp, Clock, Lock, Sparkles, ArrowRight, CheckCircle, Wallet, Gift, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const DigitalGoldPage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Lock,
      title: "99.99% Pure Gold",
      description: "Invest in 24K pure gold, stored securely in insured vaults"
    },
    {
      icon: Wallet,
      title: "Start from ₹1",
      description: "No minimum investment required. Buy gold worth even ₹1"
    },
    {
      icon: Shield,
      title: "100% Secure",
      description: "Your gold is insured and stored in high-security vaults"
    },
    {
      icon: RefreshCw,
      title: "Easy Liquidation",
      description: "Sell anytime at live market rates with instant credit"
    },
    {
      icon: Gift,
      title: "Physical Delivery",
      description: "Convert your digital gold to physical coins or bars"
    },
    {
      icon: TrendingUp,
      title: "Real-Time Prices",
      description: "Buy and sell at transparent, real-time gold rates"
    }
  ];

  const whyGold = [
    { title: "Hedge Against Inflation", desc: "Gold has historically protected wealth against rising prices" },
    { title: "Portfolio Diversification", desc: "Reduce overall risk by adding gold to your investment mix" },
    { title: "Tangible Asset", desc: "Unlike stocks, gold is a physical asset with intrinsic value" },
    { title: "Global Currency", desc: "Gold is accepted and valued worldwide" }
  ];

  const goldPrices = [
    { type: "24K Gold", price: "₹6,250/gm", change: "+0.5%" },
    { type: "22K Gold", price: "₹5,730/gm", change: "+0.4%" },
    { type: "18K Gold", price: "₹4,690/gm", change: "+0.3%" }
  ];

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/20 via-transparent to-orange-500/10" />
        <div className="absolute top-20 right-20 w-80 h-80 bg-yellow-400/20 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-60 h-60 bg-orange-500/15 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-400/10 border border-yellow-400/30 rounded-full mb-6">
                <Coins className="w-4 h-4 text-yellow-400" />
                <span className="text-yellow-400 text-sm font-medium">Digital Gold Investment</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                Invest in 
                <span className="text-yellow-400"> 24K Pure Gold</span>
                <br />Digitally
              </h1>
              <p className="text-xl text-gray-400 mb-8">
                Own real gold without the hassle of storage. Buy, sell, and gift gold 
                anytime from your phone. Start with just ₹1.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black font-bold rounded-xl hover:from-yellow-300 hover:to-yellow-400 transition-all shadow-lg shadow-yellow-400/25"
                >
                  Buy Gold Now <ArrowRight className="inline ml-2 w-5 h-5" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-white/10 text-white font-bold rounded-xl border border-white/20 hover:bg-white/20 transition-all"
                >
                  View Live Prices
                </motion.button>
              </div>
            </motion.div>

            {/* Live Prices Card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-zinc-900/80 backdrop-blur-xl border border-zinc-800 rounded-3xl p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white">Live Gold Prices</h3>
                <span className="text-xs text-gray-400">Updated just now</span>
              </div>
              <div className="space-y-4">
                {goldPrices.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-black/50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-yellow-400/20 rounded-full flex items-center justify-center">
                        <Coins className="w-5 h-5 text-yellow-400" />
                      </div>
                      <span className="text-white font-medium">{item.type}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-white font-bold">{item.price}</div>
                      <div className="text-green-400 text-sm">{item.change}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 bg-yellow-400/10 rounded-xl border border-yellow-400/30">
                <div className="flex items-center gap-2 text-yellow-400 text-sm">
                  <Sparkles className="w-4 h-4" />
                  <span>Gold prices refreshed every 30 seconds</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Why Digital Gold with iStockly?
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Experience the modern way to invest in gold - secure, convenient, and hassle-free
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 hover:border-yellow-500/50 transition-all group"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-yellow-400/20 to-orange-400/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon className="w-7 h-7 text-yellow-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Gold */}
      <section className="py-20 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Why Invest in <span className="text-yellow-400">Gold</span>?
              </h2>
              <p className="text-gray-400 mb-8">
                Gold has been a trusted store of value for thousands of years. 
                It remains one of the most reliable assets to preserve and grow your wealth.
              </p>
              <div className="space-y-4">
                {whyGold.map((item, index) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-white font-semibold">{item.title}</h4>
                      <p className="text-gray-400 text-sm">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-yellow-400/20 to-orange-500/20 rounded-3xl p-8 border border-yellow-400/30">
                <div className="text-center">
                  <div className="w-24 h-24 bg-yellow-400/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Coins className="w-12 h-12 text-yellow-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Gold Returns</h3>
                  <p className="text-gray-400 text-sm mb-6">Historical Performance</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-black/30 rounded-xl p-4">
                      <div className="text-2xl font-bold text-green-400">+12%</div>
                      <div className="text-gray-400 text-xs">1 Year</div>
                    </div>
                    <div className="bg-black/30 rounded-xl p-4">
                      <div className="text-2xl font-bold text-green-400">+45%</div>
                      <div className="text-gray-400 text-xs">3 Years</div>
                    </div>
                    <div className="bg-black/30 rounded-xl p-4">
                      <div className="text-2xl font-bold text-green-400">+85%</div>
                      <div className="text-gray-400 text-xs">5 Years</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-yellow-500/20 via-yellow-400/10 to-orange-500/20 border border-yellow-400/30 rounded-3xl p-12 text-center relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-40 h-40 bg-yellow-400/20 rounded-full blur-3xl" />
            <Sparkles className="w-16 h-16 text-yellow-400 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Start Your Gold Journey Today
            </h2>
            <p className="text-gray-300 mb-8 max-w-xl mx-auto">
              Join thousands of investors who trust iStockly for their digital gold investments. 
              Safe, secure, and simple.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-10 py-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black font-bold rounded-xl hover:from-yellow-300 hover:to-yellow-400 transition-all shadow-lg shadow-yellow-400/25"
            >
              Buy Digital Gold <ArrowRight className="inline ml-2 w-5 h-5" />
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DigitalGoldPage;
